
import { useQueryClient } from "@tanstack/react-query";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { Info, Smartphone } from "lucide-react";

export function LogoManager() {
  const { getSetting } = useSiteSettings();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const logoUrl        = getSetting("site_logo");
  const mobileHomeLink = getSetting("mobile_header_home", "linktree"); // "linktree" | "inicio"

  const save = async (key: string, value: string) => {
    try {
      await settingsApi.update(key, value);
      await queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      toast({ title: "Configuração salva!" });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-700 leading-relaxed">
          O logo é usado em todo o site: cabeçalho, rodapé, painel administrativo e
          favicon do navegador. Recomendamos uma imagem quadrada (ex: 512×512 px),
          preferencialmente PNG com fundo transparente.
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-700">Logo do Site</p>
        <p className="text-xs text-gray-400">Faça o upload e posicione o logo no recorte quadrado.</p>
        <div className="mt-3">
          <PhotoCropUpload
            value={logoUrl}
            onChange={url => save("site_logo", url)}
            shape="rect"
            aspect={1}
            label="Logo"
            previewClassName="w-20 h-20 rounded-xl border border-gray-200"
          />
        </div>
      </div>

      {/* Mobile header home link */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
          <Smartphone className="w-4 h-4 text-brand-primary" />
          <p className="text-sm font-semibold text-gray-700">Link do cabeçalho no celular</p>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          Ao clicar no logo ou no link "Início" pelo celular, para onde o visitante deve ir?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Option: Linktree */}
          <button
            type="button"
            onClick={() => save("mobile_header_home", "linktree")}
            className={`text-left border-2 rounded-xl p-4 transition-all ${
              mobileHomeLink === "linktree"
                ? "border-brand-primary bg-brand-primary/5"
                : "border-gray-200 hover:border-brand-primary/40"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {mobileHomeLink === "linktree" && (
                <span className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />
              )}
              <p className="font-semibold text-sm text-gray-800">Página Mobile (Linktree)</p>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Abre a página compacta com links rápidos — ideal para visitantes que chegam pelo Instagram ou WhatsApp.
            </p>
          </button>

          {/* Option: Início normal */}
          <button
            type="button"
            onClick={() => save("mobile_header_home", "inicio")}
            className={`text-left border-2 rounded-xl p-4 transition-all ${
              mobileHomeLink === "inicio"
                ? "border-brand-primary bg-brand-primary/5"
                : "border-gray-200 hover:border-brand-primary/40"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {mobileHomeLink === "inicio" && (
                <span className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />
              )}
              <p className="font-semibold text-sm text-gray-800">Página Início Normal</p>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Abre a home completa do site — mesma experiência do desktop.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
